import { useState } from 'react'
import { DragDropProvider, useDraggable, useDroppable } from '@dnd-kit/react'
import { AlertTriangle, ChevronLeft, ChevronRight, Cog, User } from 'lucide-react'

import type {
  ResourceConflict,
  ScheduledBlock,
  SchedulingResource,
  ProcedureSchedule,
} from '@/types/cgt'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  addDays,
  formatDayNum,
  formatWeekday,
  isWeekend,
  startOfWeek,
  weekDays,
} from '@/lib/scheduling'
import { ScheduleBlock } from './ScheduleBlock'

interface ResourceCapacityGridProps {
  schedule: ProcedureSchedule
  resources: SchedulingResource[]
  onMove: (blockId: string, move: { toDate: string; fromResourceId: string; toResourceId: string }) => void
  /** Fill the parent's height and scroll the table internally (full-screen). */
  fillHeight?: boolean
}

interface ChipData {
  blockId: string
  fromResourceId: string
}

interface CellData {
  resourceId: string
  date: string
}

// ─── Draggable chip (one block's booking of one resource) ──────────────────────

function BlockChip({
  block,
  resourceId,
  hours,
  highlighted,
  onHover,
}: {
  block: ScheduledBlock
  resourceId: string
  hours: number
  /** True when another chip of the same step is hovered/dragged. */
  highlighted: boolean
  onHover: (blockId: string | null) => void
}) {
  const { ref, isDragging } = useDraggable({
    id: `${block.id}__${resourceId}`,
    data: { blockId: block.id, fromResourceId: resourceId } satisfies ChipData,
  })
  return (
    <div
      ref={ref}
      onMouseEnter={() => onHover(block.id)}
      onMouseLeave={() => onHover(null)}
      className={cn('cursor-grab touch-none active:cursor-grabbing', isDragging && 'opacity-30')}
    >
      <ScheduleBlock
        block={block}
        laneHours={hours}
        draggable
        className={cn(
          'transition-[outline]',
          highlighted && 'outline outline-2 outline-offset-1 outline-primary',
        )}
      />
    </div>
  )
}

// ─── Droppable day cell for a resource lane ────────────────────────────────────

function DayCell({
  resourceId,
  date,
  isAnchor,
  weekend,
  children,
  conflict,
}: {
  resourceId: string
  date: string
  isAnchor: boolean
  weekend: boolean
  children: React.ReactNode
  conflict?: ResourceConflict
}) {
  const { ref, isDropTarget } = useDroppable({
    id: `${resourceId}__${date}`,
    data: { resourceId, date } satisfies CellData,
  })
  return (
    <td
      ref={ref}
      className={cn(
        'h-[4.25rem] min-w-[8.75rem] border-b border-l border-border/50 p-1.5 align-top transition-colors',
        weekend && 'bg-muted/30',
        isAnchor && 'bg-primary/[0.04]',
        conflict && 'bg-destructive/[0.06]',
        isDropTarget && 'bg-primary/10 outline-dashed outline-2 -outline-offset-2 outline-primary',
      )}
    >
      <div className="flex flex-col gap-1">{children}</div>
      {conflict && (
        <div className="mt-1 inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-destructive">
          <AlertTriangle className="size-2.5" />
          {conflict.allocatedHours}/{conflict.capacityHours}h
        </div>
      )}
    </td>
  )
}

export function ResourceCapacityGrid({
  schedule,
  resources,
  onMove,
  fillHeight,
}: ResourceCapacityGridProps) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(schedule.anchorDate))
  const [hoverBlockId, setHoverBlockId] = useState<string | null>(null)
  const [dragBlockId, setDragBlockId] = useState<string | null>(null)
  const days = weekDays(weekStart)

  // A step books several resource lanes on the same day, so it renders as one
  // chip per lane. Highlight every chip of the step being hovered or dragged so
  // it reads as a single unit (not duplicates) when it moves.
  const highlightId = dragBlockId ?? hoverBlockId

  const conflictByCell = new Map<string, ResourceConflict>()
  for (const c of schedule.conflicts) conflictByCell.set(`${c.resourceId}|${c.date}`, c)

  function chipsFor(resourceId: string, date: string) {
    const result: { block: ScheduledBlock; hours: number }[] = []
    for (const block of schedule.blocks) {
      if (block.date !== date) continue
      const allocation = block.allocations.find((a) => a.resourceId === resourceId)
      if (allocation) result.push({ block, hours: allocation.hours })
    }
    return result
  }

  const equipment = resources.filter((r) => r.kind === 'equipment')
  const staff = resources.filter((r) => r.kind === 'staff')
  const groups: { label: string; icon: typeof Cog; rows: SchedulingResource[] }[] = [
    { label: 'Equipment', icon: Cog, rows: equipment },
    { label: 'Staff', icon: User, rows: staff },
  ].filter((g) => g.rows.length > 0)

  const colCount = days.length + 1

  return (
    <div className={cn('space-y-3', fillHeight && 'flex h-full min-h-0 flex-col space-y-0 gap-3')}>
      <div className={cn('flex items-center justify-between', fillHeight && 'shrink-0')}>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold">{formatWeekday(weekStart)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">
            Week of {days[0] && new Date(days[0]).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' })}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => setWeekStart((w) => addDays(w, -7))}
            aria-label="Previous week"
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setWeekStart(startOfWeek(schedule.anchorDate))}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => setWeekStart((w) => addDays(w, 7))}
            aria-label="Next week"
          >
            <ChevronRight />
          </Button>
        </div>
      </div>

      <DragDropProvider
        onDragStart={(event) => {
          const chip = event.operation.source?.data as ChipData | undefined
          setDragBlockId(chip?.blockId ?? null)
        }}
        onDragEnd={(event) => {
          setDragBlockId(null)
          const { source, target } = event.operation
          if (event.canceled || !source || !target) return
          const chip = source.data as ChipData
          const cell = target.data as CellData
          if (
            chip.fromResourceId === cell.resourceId &&
            chipDateUnchanged(schedule, chip.blockId, cell.date)
          ) {
            return
          }
          onMove(chip.blockId, {
            toDate: cell.date,
            fromResourceId: chip.fromResourceId,
            toResourceId: cell.resourceId,
          })
        }}
      >
        <div
          className={cn(
            'overflow-x-persistent rounded-xl border bg-card shadow-sm',
            fillHeight && 'min-h-0 flex-1 overflow-x-persistent',
          )}
        >
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th
                  className={cn(
                    'sticky left-0 z-20 min-w-[11rem] bg-table-header px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-table-header-foreground',
                    fillHeight && 'top-0',
                  )}
                >
                  Resource
                </th>
                {days.map((date) => {
                  const anchor = date === schedule.anchorDate
                  return (
                    <th
                      key={date}
                      className={cn(
                        'border-l border-border/50 bg-table-header px-2 py-2 text-center text-table-header-foreground',
                        fillHeight && 'sticky top-0 z-10',
                        isWeekend(date) && 'bg-table-header/90',
                      )}
                    >
                      <div className="text-[10px] font-medium uppercase tracking-wide text-table-header-foreground/80">
                        {formatWeekday(date)}
                      </div>
                      <div
                        className={cn(
                          'mx-auto mt-0.5 flex size-7 items-center justify-center rounded-full text-sm font-semibold tabular-nums',
                          anchor && 'bg-table-header-foreground text-table-header',
                        )}
                      >
                        {formatDayNum(date)}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <GroupRows
                  key={group.label}
                  group={group}
                  days={days}
                  colCount={colCount}
                  anchorDate={schedule.anchorDate}
                  conflictByCell={conflictByCell}
                  chipsFor={chipsFor}
                  highlightId={highlightId}
                  onHover={setHoverBlockId}
                />
              ))}
            </tbody>
          </table>
        </div>
      </DragDropProvider>
    </div>
  )
}

function GroupRows({
  group,
  days,
  colCount,
  anchorDate,
  conflictByCell,
  chipsFor,
  highlightId,
  onHover,
}: {
  group: { label: string; icon: typeof Cog; rows: SchedulingResource[] }
  days: string[]
  colCount: number
  anchorDate: string
  conflictByCell: Map<string, ResourceConflict>
  chipsFor: (resourceId: string, date: string) => { block: ScheduledBlock; hours: number }[]
  highlightId: string | null
  onHover: (blockId: string | null) => void
}) {
  const Icon = group.icon
  return (
    <>
      <tr>
        <td
          colSpan={colCount}
          className="sticky left-0 z-10 border-b border-t border-border/50 bg-muted/30 px-3 py-1.5"
        >
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Icon className="size-3.5" />
            {group.label}
            <span className="font-normal normal-case text-muted-foreground/70">
              ({group.rows.length})
            </span>
          </span>
        </td>
      </tr>
      {group.rows.map((resource) => (
        <tr key={resource.id} className="group/row">
          <th className="sticky left-0 z-10 min-w-[11rem] border-b border-border/50 bg-card px-3 py-2 text-left align-middle transition-colors group-hover/row:bg-muted/40">
            <div className="text-sm font-medium text-foreground">{resource.name}</div>
            <div className="text-[10px] font-normal tabular-nums text-muted-foreground">
              cap {resource.capacityHoursPerDay} hrs/day
            </div>
          </th>
          {days.map((date) => (
            <DayCell
              key={date}
              resourceId={resource.id}
              date={date}
              isAnchor={date === anchorDate}
              weekend={isWeekend(date)}
              conflict={conflictByCell.get(`${resource.id}|${date}`)}
            >
              {chipsFor(resource.id, date).map(({ block, hours }) => (
                <BlockChip
                  key={block.id}
                  block={block}
                  resourceId={resource.id}
                  hours={hours}
                  highlighted={block.id === highlightId}
                  onHover={onHover}
                />
              ))}
            </DayCell>
          ))}
        </tr>
      ))}
    </>
  )
}

/** True if the block already sits on `date` (so a same-lane, same-day drop is a no-op). */
function chipDateUnchanged(
  schedule: ProcedureSchedule,
  blockId: string,
  date: string,
): boolean {
  const block = schedule.blocks.find((b) => b.id === blockId)
  return block?.date === date
}
